import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

/** Read the theme currently applied to <html>. */
function currentTheme(): Theme {
    return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
}

/**
 * Theme controller. Defaults to the OS preference (handled by the inline
 * script in index.html); once the user toggles, the choice is persisted to
 * localStorage and overrides the OS. While there is no saved override, the
 * UI keeps following live OS changes.
 */
export function useTheme(): [Theme, () => void] {
    const [theme, setTheme] = useState<Theme>(currentTheme);

    const apply = useCallback((t: Theme) => {
        document.documentElement.setAttribute("data-theme", t);
        try {
            localStorage.setItem("theme", t);
        } catch (e) { /* ignore */ }
        setTheme(t);
    }, []);

    const toggle = useCallback(() => {
        apply(currentTheme() === "dark" ? "light" : "dark");
    }, [apply]);

    useEffect(() => {
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = (e: MediaQueryListEvent) => {
            let saved: string | null = null;
            try {
                saved = localStorage.getItem("theme");
            } catch (err) { /* ignore */ }
            if (saved !== "light" && saved !== "dark") {
                const next: Theme = e.matches ? "dark" : "light";
                document.documentElement.setAttribute("data-theme", next);
                setTheme(next);
            }
        };
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, []);

    return [theme, toggle];
}
